import React from 'react';
import { McInput, McSelect, McButton, McIcon } from '@maersk-global/mds-react-wrapper';

const TYPE_OPTIONS = [
    { label: 'String', value: 'STRING' },
    { label: 'Number', value: 'NUMBER' },
    { label: 'Boolean', value: 'BOOLEAN' },
    { label: 'Date', value: 'DATE' },
    { label: 'Timestamp', value: 'TIMESTAMP' },
    { label: 'String List (IN)', value: 'STRING_LIST' },
];

interface ParameterRowProps {
    name: string;
    type?: string;
    testValue?: string;
    description?: string;
    onNameChange?: (value: string) => void;
    onTypeChange?: (value: string) => void;
    onTestValueChange?: (value: string) => void;
    onDescriptionChange?: (value: string) => void;
    onDelete?: () => void;
}

/**
 * Compact single-row parameter editor shared between Create Query and Edit Query.
 * Shows/hides columns based on which callbacks are provided:
 *   - Name column: shown when onNameChange is provided (Create), otherwise shows read-only badge
 *   - Type column: shown when onTypeChange is provided (Edit)
 *   - Delete button: shown when onDelete is provided (Create)
 */
const ParameterRow: React.FC<ParameterRowProps> = ({
    name,
    type,
    testValue,
    description,
    onNameChange,
    onTypeChange,
    onTestValueChange,
    onDescriptionChange,
    onDelete,
}) => {
    return (
        <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 8,
            padding: '8px 12px',
            borderBottom: '1px solid #f3f4f6',
        }}>
            {/* Badge or editable name */}
            {onNameChange ? (
                <div style={{ flex: '0 0 140px' }}>
                    <McInput
                        label="Name"
                        value={name}
                        onInput={(e: any) => onNameChange(e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                    />
                </div>
            ) : (
                <div style={{
                    flex: '0 0 auto',
                    alignSelf: 'center',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: 12,
                    color: '#0073ab',
                    whiteSpace: 'nowrap',
                }}>
                    :{name}
                </div>
            )}

            {/* Type — only for Edit Query */}
            {onTypeChange && (
                <div style={{ flex: '0 0 130px' }}>
                    <McSelect
                        label="Type"
                        value={type}
                        data={TYPE_OPTIONS.map(o => ({ ...o }))}
                        optionselected={(e: any) => onTypeChange(e.detail.value)}
                    />
                </div>
            )}

            {/* Test Value */}
            <div style={{ flex: 1 }}>
                <McInput
                    label="Test Value"
                    value={testValue ?? ''}
                    placeholder={type === 'DATE' ? 'YYYY-MM-DD' : 'Value'}
                    onInput={(e: any) => onTestValueChange?.(e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                />
            </div>

            {/* Description */}
            <div style={{ flex: 2 }}>
                <McInput
                    label="Description"
                    value={description ?? ''}
                    placeholder="Metadata info"
                    onInput={(e: any) => onDescriptionChange?.(e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                />
            </div>

            {/* Delete — only for Create Query */}
            {onDelete && (
                <McButton
                    variant="plain"
                    appearance="error"
                    fit="small"
                    style={{ minWidth: 36, padding: '0 8px', marginBottom: 2 }}
                    onClick={onDelete}
                >
                    <McIcon icon="trash" />
                </McButton>
            )}
        </div>
    );
};

export default ParameterRow;
